import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { authenticatedPageDecorator, pageTitleDecorator } from 'Decorators';
import SettingsInvitationSend from './SettingsInvitationSend';
import { loadInvitations, updateFilter, loadNameAutocomplete } from 'Actions/invitation';
import { DataTableMetadataType, InvitationType, QueryFilterType } from 'Types';
import {
    DATA_TABLE_COLUMN_TYPE_DATE,
    DATA_TABLE_COLUMN_TYPE_LINK,
    DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE,
    ROLE_ADMIN,
    ROLE_EDITORIAL,
    ROLE_CHIEF_EDITORIAL,
    ROLE_EDITOR,
    ROLES_HAS_JOURNAL,
} from 'Constants';

import DataTable from 'Components/DataTable';
import { loadAutocomplete as loadJournalsAutocomplete } from 'Actions/journal';
import Header from 'Components/Header/Header';

@authenticatedPageDecorator([ROLE_ADMIN, ROLE_EDITORIAL, ROLE_CHIEF_EDITORIAL, ROLE_EDITOR])
@connect(
    store => ({
        user: store.auth.user,
        data: store.invitationList.data,
        filter: store.invitationList.filter,
        metadata: store.invitationList.metadata,
        journalsAutocompleteFilter: store.journalsListAutocomplete.filter,
        journalsAutocompleteData: store.journalsListAutocomplete.data,
        usersAutocompleteFilter: store.invitationNameAutocomplete.filter,
        usersAutocompleteData: store.invitationNameAutocomplete.names,
    }),
    dispatch => ({
        loadInvitations: filter => dispatch(loadInvitations(filter)),
        updateFilter: filter => dispatch(updateFilter(filter)),
        loadJournalsOptions: filter => dispatch(loadJournalsAutocomplete(filter)),
        loadUsersOptions: filter => dispatch(loadNameAutocomplete(filter)),
    })
)
@pageTitleDecorator('settings.invitations')
export default class SettingsInvitationsList extends PureComponent {
    /**
     * PropTypes
     */
    static propTypes = {
        user: PropTypes.object.isRequired,
        data: PropTypes.arrayOf(InvitationType),
        filter: QueryFilterType,
        metadata: DataTableMetadataType,
        journalsAutocompleteFilter: PropTypes.object.isRequired,
        journalsAutocompleteData: PropTypes.array.isRequired,
        usersAutocompleteFilter: PropTypes.object.isRequired,
        usersAutocompleteData: PropTypes.array.isRequired,
        loadInvitations: PropTypes.func.isRequired,
        updateFilter: PropTypes.func.isRequired,
        loadJournalsOptions: PropTypes.func.isRequired,
        loadUsersOptions: PropTypes.func.isRequired,
        is: PropTypes.func.isRequired,
        isNot: PropTypes.func.isRequired,
        isOneOf: PropTypes.func.isRequired,
    };

    /**
     * Data table config
     *
     * @returns {*[]}
     */
    getColumns = () => {
        const { journalsAutocompleteData, usersAutocompleteData } = this.props;

        return [
            {
                key: 'name',
                col: { width: '20%' },
                title: 'data_table.name',
                sortable: true,
                sortableKey: 'first_name',
                filterable: true,
                filterKey: 'full_name',
                filterType: DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE,
                filterPlaceholder: 'data_table.filter.name',
                options: usersAutocompleteData,
                loadOptions: this.loadUsersOptions,
            },
            {
                key: 'email',
                col: { width: '20%' },
                title: 'data_table.email',
                sortable: true,
            },
            {
                key: 'journal.title',
                col: { width: '40%' },
                title: 'data_table.journal_id',
                type: DATA_TABLE_COLUMN_TYPE_LINK,
                link: {
                    to: '/journals/:id',
                    placeholders: {
                        id: item => item.journal_id,
                    },
                },
                filterable: true,
                sortable: true,
                filterKey: 'journal_id',
                filterType: DATA_TABLE_FILTER_TYPE_AUTOCOMPLETE_SINGLE,
                filterPlaceholder: 'data_table.filter.journal_id',
                options: journalsAutocompleteData.map(item => ({
                    value: item.id,
                    label: item.title,
                })),
                loadOptions: this.loadJournalsOptions,
            },
            {
                key: 'status',
                col: { width: '10%' },
                title: 'data_table.invitation.status',
                sortable: true,
            },
            {
                key: 'created_at',
                filterKey: 'invitations.created_at',
                col: { width: '10%' },
                title: 'data_table.created_at',
                sortable: true,
                type: DATA_TABLE_COLUMN_TYPE_DATE,
            },
        ];
    };

    /**
     * Get filter for role
     */
    getFilter = () => {
        const { is, isOneOf } = this.props;
        let { filter } = this.props;

        if (is(ROLE_ADMIN)) {
            return filter;
        }

        const {
            user: { id },
        } = this.props;

        filter = filter.merge({ where: { user_id: id } }, { deep: true });

        if (isOneOf(ROLES_HAS_JOURNAL)) {
            const {
                user: {
                    activeRole: { journals },
                },
            } = this.props;

            filter = filter.merge({ where: { journal_id: { inq: journals } } }, { deep: true });
        }

        return filter;
    };

    /**
     * Load journals
     *
     * @param input
     */
    loadJournalsOptions = input => {
        const { journalsAutocompleteFilter, loadJournalsOptions, isOneOf } = this.props;

        let filter = journalsAutocompleteFilter.merge({
            where: { title: { like: `%${input}%` } },
        });

        if (isOneOf(ROLES_HAS_JOURNAL)) {
            const {
                user: {
                    activeRole: { journals },
                },
            } = this.props;

            filter = filter.merge({ where: { id: { inq: journals } } }, { deep: true });
        }

        loadJournalsOptions(filter);
    };

    /**
     * Load users
     *
     * @param input
     */
    loadUsersOptions = input => {
        const { usersAutocompleteFilter, loadUsersOptions, isOneOf, isNot } = this.props;

        const or = [];
        input.split(' ').forEach(word => {
            if (word.length) {
                or.push({ first_name: { like: `%${word}%` }, last_name: { like: `%${word}%` } });
            }
        });

        let filter = usersAutocompleteFilter.merge(
            {
                where: { or },
            },
            { deep: true }
        );

        if (isNot(ROLE_ADMIN)) {
            const {
                user: { id },
            } = this.props;

            filter = filter.merge({ where: { user_id: id } }, { deep: true });
            if (isOneOf(ROLES_HAS_JOURNAL)) {
                const {
                    user: {
                        activeRole: { journals },
                    },
                } = this.props;

                filter = filter.merge({ where: { journal_id: { inq: journals } } }, { deep: true });
            }
        }
        loadUsersOptions(filter);
    };

    /**
     * Render send form
     *
     * @returns {*}
     */
    renderSendForm = () => {
        const { isNot } = this.props;

        return isNot(ROLE_EDITOR) && <SettingsInvitationSend />;
    };

    /**
     * Render
     *
     * @returns {*}
     */
    render() {
        const { data, loadInvitations, metadata, updateFilter } = this.props;

        return (
            <div className="wrapJournalList">
                <Header title="title.invitations">
                    {this.renderSendForm()}
                </Header>

                <DataTable
                    columns={this.getColumns()}
                    metadata={metadata}
                    data={data}
                    filter={this.getFilter()}
                    load={loadInvitations}
                    updateFilter={updateFilter}
                    icon={false}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/layouts/Settings/containers/SettingsInvitationsList.jsx